import React, { useState, useEffect } from 'react';
import ldapService from '../../services/ldapService';

// Icon components
const ServerIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 17.25v-.228a4.5 4.5 0 00-.12-1.03l-2.268-9.64a3.375 3.375 0 00-3.285-2.602H7.923a3.375 3.375 0 00-3.285 2.602l-2.268 9.64a4.5 4.5 0 00-.12 1.03v.228m21.75 0a3 3 0 01-3 3H5.25a3 3 0 01-3-3m21.75 0v-1.5a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v1.5m0 0h21v-2.25" />
  </svg>
);

const CheckCircleIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InformationCircleIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>
);

const RefreshIcon = ({ className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const LdapStatusComponent = ({ compact = false }) => {
  const [ldapStatus, setLdapStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchLdapStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await ldapService.getLdapStatus();
      if (result.success) {
        setLdapStatus(result.data);
        setLastChecked(new Date());
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to fetch LDAP status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLdapStatus();
  }, []);

  if (compact) {
    return (
      <div className="flex items-center space-x-2 text-sm">
        {loading ? (
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sunrise-red"></div>
            <span className="text-gray-500">Checking LDAP...</span>
          </div>
        ) : ldapStatus?.ldapEnabled ? (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircleIcon className="w-4 h-4" />
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-gray-500">
            <InformationCircleIcon className="w-4 h-4" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="card-brand p-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <ServerIcon className="w-6 h-6 text-sunrise-red" />
          <h3 className="text-lg font-semibold text-grey-dusk">Authentication Status</h3>
        </div>
        <button
          onClick={fetchLdapStatus}
          disabled={loading}
          className="p-2 text-gray-500 hover:text-sunrise-red transition-colors duration-200 disabled:opacity-50"
          title="Refresh status"
        >
          <RefreshIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !ldapStatus ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sunrise-red"></div>
          <span className="ml-3 text-gray-600">Checking authentication status...</span>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <XCircleIcon className="w-5 h-5 text-red-500" />
            <span className="text-red-700 font-medium">Unable to check status</span>
          </div>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* LDAP Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              {ldapStatus.ldapEnabled ? (
                <CheckCircleIcon className="w-6 h-6 text-green-500" />
              ) : (
                <XCircleIcon className="w-6 h-6 text-gray-400" />
              )}
              <div>
                <h4 className="font-medium text-gray-900">LDAP Authentication</h4>
                <p className="text-sm text-gray-600">
                  {ldapStatus.ldapEnabled ? 'Active Directory integration enabled' : 'Disabled - database authentication only'}
                </p>
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              ldapStatus.ldapEnabled 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {ldapStatus.ldapEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          {/* LDAP Details */}
          {ldapStatus.ldapEnabled && (
            <div className="space-y-3">
              {/* Domain */}
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-700">Domain</span>
                <span className="text-sm text-gray-900 font-mono">
                  {ldapService.formatBaseDn(ldapStatus.ldapBase)}
                </span>
              </div>

              {/* Servers */}
              <div className="py-2">
                <span className="text-sm font-medium text-gray-700 block mb-2">LDAP Servers</span>
                <div className="space-y-2">
                  {ldapService.formatLdapServers(ldapStatus.ldapUrls).map((server, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                      <span className="font-mono text-blue-900">{server.server}</span>
                      <span className="text-blue-600">:{server.port}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Authentication Methods */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Available Authentication Methods</h4>
            <div className="space-y-2">
              {ldapStatus.ldapEnabled && (
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-gray-700">Active Directory (lab.local domain)</span>
                </div>
              )}
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-gray-700">Database Users</span>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          {lastChecked && (
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              Last updated: {lastChecked.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LdapStatusComponent;